/**
 * New ▾'s sheets — `docs/activity.md` §9 (Boards 11, 16 D–E, 20 A).
 *
 * The words the sheets' footers and fields say, worked out the same way on
 * both sides, and the answers of the three endpoints only the sheets ask
 * (`GET /activity/new-setup`, `/activity/compose`, `/activity/meeting-fit`).
 * Every number in them is the server's (rule 2); these only say them.
 */
import { clock, clockBare, dayComma, minutesWords, weekdayLong } from '../requests/words.js';
import { stopLabel } from '../requests/naming.js';
import { firstNameOf, PERSON_COLOURS } from './people.js';
import { presetKeyOf, recurrenceOf, repeatEndWords, repeatPatternWords } from './recurrence.js';
// ---------------------------------------------------------------------------
// People and places, said shortly
// ---------------------------------------------------------------------------
/**
 * A contact's tone: the eight people colours, chosen by the id so the same
 * customer is the same colour on every screen (the Inbox's rule, §4.5).
 */
export function contactTone(id) {
    let hash = 0;
    for (const char of id)
        hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
    return PERSON_COLOURS[hash % PERSON_COLOURS.length].edge;
}
/**
 * A stop's name where there is room for a word (Board 11 F's day strip,
 * Board 20 A's finder, *14 min from Meridian*): *Riverfront Office Park* →
 * *Riverfront*, *Renee Castillo Residence* → *Castillo*.
 */
export function shortStopName(title) {
    const trimmed = title.trim();
    const words = stopLabel(trimmed).split(/\s+/).filter(Boolean);
    if (words.length <= 1)
        return words[0] ?? trimmed;
    return /\sresidence$/i.test(trimmed) ? words[words.length - 1] : words[0];
}
export function recordWords(record) {
    if (record.type === 'contact')
        return firstNameOf(record.label);
    if (record.type === 'property' && /\sresidence$/i.test(record.label.trim()))
        return 'the residence';
    return record.label;
}
/** *Renee, the residence and J-3318*. */
export function listWords(parts) {
    if (parts.length <= 1)
        return parts[0] ?? '';
    return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}
/** *Shows on Renee, the residence and J-3318* (Boards 11 B, G). */
export function showsOnWords(records) {
    if (records.length === 0)
        return '';
    return `Shows on ${listWords(records.map(recordWords))}`;
}
// ---------------------------------------------------------------------------
// The task and the meeting
// ---------------------------------------------------------------------------
/** *On Tom’s calendar Mon, Oct 26 at 9:00 AM* · *Due Mon, Oct 26* · *No due date* (§9.5). */
export function taskFooterWords(input) {
    if (!input.date)
        return 'No due date';
    if (input.minutes === null)
        return `Due ${dayComma(input.date)}`;
    const whose = input.firstNames.length > 1 ? `${input.firstNames.length} calendars`
        : `${input.firstNames[0] ?? 'your'}’s calendar`;
    return `On ${whose} ${dayComma(input.date)} at ${clock(input.minutes)}`;
}
/** *1:30–2:30 PM*, *8:00–9:00 AM*, *11:30 AM–12:30 PM*. */
export function meetingRange(start, end) {
    const half = (m) => (Math.floor(m / 60) % 24 < 12 ? 'AM' : 'PM');
    return half(start) === half(end) ? `${clockBare(start)}–${clock(end)}` : `${clock(start)}–${clock(end)}`;
}
/**
 * *On Tom’s calendar · Tue, Oct 27 · 1:30–2:30 PM* · *On 5 calendars · Tue,
 * Oct 27 · 8:00–9:00 AM, then every Tuesday* (§9.6).
 */
export function meetingFooterWords(input) {
    const whose = input.firstNames.length > 1 ? `${input.firstNames.length} calendars`
        : `${input.firstNames[0] ?? 'your'}’s calendar`;
    const recurrence = input.recurrence !== undefined ? input.recurrence : recurrenceOf({ rule: input.repeatRule });
    const preset = presetKeyOf(recurrence);
    const end = recurrence ? repeatEndWords(recurrence) : '';
    const then = !recurrence ? ''
        : preset === 'weekly' ? `, then every ${weekdayLong(input.date)}`
            : preset === 'every_other_week' ? `, then every other ${weekdayLong(input.date)}`
                : preset === 'monthly' ? ', then every month'
                    : preset === 'daily' ? ', then every day'
                        : `, then ${repeatPatternWords(input.date, recurrence)}${end ? `, ${end}` : ''}`;
    return `On ${whose} · ${dayComma(input.date)} · ${meetingRange(input.start, input.start + input.minutes)}${then}`;
}
/** *Repeats* for a meeting (§9.6): *Never · Every week on <weekday> · Every other week · Every month*. */
export function meetingRepeatOptions(date) {
    return [
        { rule: null, label: 'Never' },
        { rule: 'weekly', label: `Every week on ${weekdayLong(date)}` },
        { rule: 'every_other_week', label: 'Every other week' },
        { rule: 'monthly', label: 'Every month' },
    ];
}
/** A meeting's *Length* (Board 11 F's *1 h*). */
export const MEETING_LENGTHS = [15, 30, 45, 60, 90, 120, 180, 240];
/** *1 h*, *30 min*, *1 h 30 min*. */
export const lengthWords = (minutes) => minutesWords(minutes);
/**
 * The people field's right-hand words (§9.6): *free then* · *5 · all free
 * then* · *Tom is busy then* · *Tom and Luis are busy then*.
 */
export function goingWords(people) {
    if (people.length === 0)
        return '';
    const busy = people.filter((person) => !person.free);
    if (busy.length === 0)
        return people.length === 1 ? 'free then' : `${people.length} · all free then`;
    return `${listWords(busy.map((person) => person.firstName))} ${busy.length === 1 ? 'is' : 'are'} busy then`;
}
/** The finder's column: *All 5 free · 8:00–9:00* or *4 of 5 free · 8:00–9:00* (Board 20 A). */
export function finderWords(free, of, start, end) {
    const when = `${clockBare(start)}–${clockBare(end)}`;
    return free === of ? `All ${of} free · ${when}` : `${free} of ${of} free · ${when}`;
}
//# sourceMappingURL=new.js.map